// Cabecera de la pantalla de detalle: identidad del pedido y sus acciones.
//
// El número manda; el resto de la identidad va en una línea de datos por debajo,
// separados por puntos, para que ocupe poco y se lea de un barrido. Las acciones
// abren los mismos cajones que la lista: aquí no se rehace ningún flujo.
import { useTranslation } from 'react-i18next'
import { StatusBadge } from '@/components/common/StatusBadge'
import { RefreshButton } from '@/components/common/RefreshButton'
import { fmtDate } from '../formato'
import {
  apagadoCls, avisoCls, bordeCls, btnNeutroCls, btnPrimarioCls, rotuloCls, textoCls,
} from './estilos'
import type { PedidoVentaCabeceraVM } from '@/types'

interface Props {
  dark: boolean
  cabecera: PedidoVentaCabeceraVM
  isEmpresaAll: boolean
  /** Datos incompletos: la cabecera se ha reconstruido de las líneas. */
  parcial?: boolean
  /** El pedido admite cargas nuevas (está abierto). */
  puedeCrearCarga?: boolean
  isFetching?: boolean
  onVolver: () => void
  onAdjuntos: () => void
  /** Lleva a la sección de documentación. La generación documental es POR CARGA
   *  —no existe a nivel de pedido—, así que desde aquí se acompaña hasta donde
   *  están las cargas en vez de abrir un cajón que no sabría de cuál hablar. */
  onDocumentos: () => void
  onNuevaCarga: () => void
  /** Descarga el mismo libro que la lista, con este pedido solo. Vive en la
   *  cabecera y no dentro de una pestaña: exporta el pedido ENTERO, así que
   *  depender de qué se está mirando sería mentir sobre lo que baja. */
  onExportar: () => void
  exportando?: boolean
  onRefrescar: () => void
}

export function CabeceraPedido({
  dark, cabecera: c, isEmpresaAll, parcial, puedeCrearCarga, isFetching,
  onVolver, onAdjuntos, onDocumentos, onNuevaCarga, onExportar, exportando,
  onRefrescar,
}: Props) {
  const { t } = useTranslation()

  // Una entrega vencida sólo es un problema si queda algo por servir: un pedido
  // completado tarde ya no requiere ninguna acción.
  const vencida = !!c.fechaEntrega
    && c.fechaEntrega < new Date().toISOString().slice(0, 10)
    && c.qtyPendiente > 0

  const dato = (etiqueta: string, valor?: string | null) => valor
    ? (
      <span className="inline-flex items-baseline gap-1.5">
        <span className={rotuloCls(dark)}>{etiqueta}</span>
        <span className={`text-[12px] font-medium ${textoCls(dark)}`}>{valor}</span>
      </span>
    )
    : null

  return (
    <header className={`flex flex-col gap-3 pb-4 border-b ${bordeCls(dark)}`}>
      <button
        type="button"
        onClick={onVolver}
        className={`self-start inline-flex items-center gap-1.5 text-[11px] font-medium cursor-pointer transition-colors ${
          dark ? 'text-slate-400 hover:text-slate-200' : 'text-slate-500 hover:text-slate-800'
        }`}
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9.5 3.5L5 8l4.5 4.5" />
        </svg>
        {t('salesOrders.title')}
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex flex-col gap-2.5 min-w-0">
          {/* El número a 30 px: es la identidad de la pantalla, y a 22 px pesaba
              lo mismo que el título de cualquier lista. */}
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className={`font-mono text-[30px] font-black tracking-[-0.02em] leading-none ${dark ? 'text-slate-50' : 'text-slate-900'}`}>
              {c.salesid}
            </h1>
            {c.estado && (
              <StatusBadge status={c.estado} dark={dark} size="sm" namespace="salesOrders" />
            )}
            {isEmpresaAll && (
              <span className={`text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded ${
                dark ? 'bg-white/8 text-slate-300' : 'bg-slate-100 text-slate-600'
              }`}>
                {c.empresa}
              </span>
            )}
            {/* En plano y con icono. Un tinte translúcido al lado de un número
                de 30 px no se ve, y una entrega vencida es exactamente lo que
                el usuario tiene que ver antes de leer nada. */}
            {vencida && (
              <span className={avisoCls('rojo', 'fuerte', dark)}>
                <svg className="w-3 h-3 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M8 1.8L15 14H1L8 1.8z" /><path d="M8 6.5v3.2" /><path d="M8 11.8h.01" />
                </svg>
                {t('salesOrders.overdue')}
              </span>
            )}
            {c.pendienteSync && (
              <span className={avisoCls('ambar', 'fuerte', dark)}>
                {t('salesOrders.sync.badge')}
              </span>
            )}
          </div>

          {/* Identidad en dos alturas: el cliente es por quien se pregunta y va
              solo, con cuerpo propio; la referencia y las fechas son consulta y
              bajan un escalón. Antes iban los cuatro campos en la misma línea y
              el cliente no se distinguía de un dato más. */}
          {(c.nombreCliente || c.custaccount) && (
            <p className={`text-[15px] font-semibold leading-none ${dark ? 'text-slate-100' : 'text-slate-800'}`}>
              {c.nombreCliente || c.custaccount}
              {c.nombreCliente && c.custaccount && (
                <span className={`ml-2 font-mono text-[11px] font-normal ${apagadoCls(dark)}`}>
                  {c.custaccount}
                </span>
              )}
            </p>
          )}
          <div className="flex items-baseline gap-x-4 gap-y-1 flex-wrap">
            {dato(t('salesOrders.col.customerRef'), c.customerRef)}
            {dato(t('salesOrders.col.customerGroup'), c.grupoCliente ?? undefined)}
            {dato(t('salesOrders.col.orderDate'), fmtDate(c.fechaPedido))}
            {dato(t('salesOrders.col.deliveryDate'), fmtDate(c.fechaEntrega))}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button type="button" onClick={onAdjuntos} className={btnNeutroCls(dark)}>
            {t('attachments.title')}
          </button>
          <button type="button" onClick={onDocumentos} className={btnNeutroCls(dark)}>
            {t('salesOrders.documentos.title')}
          </button>
          <button
            type="button"
            onClick={onExportar}
            disabled={exportando}
            className={`${btnNeutroCls(dark)} disabled:opacity-50 disabled:cursor-wait`}
          >
            {exportando ? t('common.exporting') : t('common.exportExcel')}
          </button>
          {puedeCrearCarga && (
            <button type="button" onClick={onNuevaCarga} className={btnPrimarioCls}>
              {t('salesOrders.nuevaCarga.boton')}
            </button>
          )}
          <RefreshButton dark={dark} onClick={onRefrescar} isLoading={isFetching} />
        </div>
      </div>

      {parcial && (
        <p className={`text-[11px] ${dark ? 'text-amber-300' : 'text-amber-700'}`}>
          {t('salesOrders.detalle.cabeceraParcial')}
        </p>
      )}
    </header>
  )
}
